import { HelpScreenshot } from '../components/dialogs/HelpScreenshot';

// Sample screenshot data - in a real implementation, this would come from the backend
const helpScreenshots: HelpScreenshot[] = [
  {
    id: 'load-document',
    title: 'Loading a Document from .nbr File',
    description: 'This visual guide shows you exactly how to load a document from a .nbr file created by the Narralyzer desktop application. Follow the numbered steps below.',
    imagePath: '/help-screenshots/document-management/load-document.png',
    animatedImagePath: '/help-screenshots/document-management/load-document-animated.gif',
    altText: 'Screenshot showing the Load Document feature in the document panel with file selection dialog open, including step-by-step visual guide with highlighted areas',
    steps: [
      'Click on the "Load Document" button in the document panel (highlighted in green)',
      'Select a .nbr file from your computer using the file browser',
      'The document will be imported and opened in a new tab automatically',
      'You can now work with the loaded document alongside other open documents'
    ]
  },
  {
    id: 'create-document',
    title: 'Creating a New Document',
    description: 'Learn how to create a new document from scratch and start organizing your content.',
    imagePath: '/help-screenshots/document-management/create-document.png',
    animatedImagePath: '/help-screenshots/document-management/create-document-animated.gif',
    altText: 'Screenshot showing the Create Document dialog with form fields for document name and description',
    steps: [
      'Click the "Create Document" button in the document panel',
      'Enter a name for your new document',
      'Add an optional description to help identify the document',
      'Click "Create" to start working with your new document'
    ]
  },
  {
    id: 'add-category',
    title: 'Adding Categories to Your Document',
    description: 'Organize your content by creating categories and subcategories.',
    imagePath: '/help-screenshots/category-management/add-category.png',
    altText: 'Screenshot showing the Add Category dialog with form fields for category name and description',
    steps: [
      'Select a parent category where you want to add the new category',
      'Click the "+" button next to the category name',
      'Enter a name and description for your new category',
      'Choose whether it will contain subcategories or content',
      'Click "Add" to create the category'
    ]
  },
  {
    id: 'edit-content',
    title: 'Editing Content in Categories',
    description: 'Add and edit text content within your categories.',
    imagePath: '/help-screenshots/content-editor/edit-content.png',
    altText: 'Screenshot showing the content editor with rich text formatting toolbar and editing area',
    steps: [
      'Click on a content category to open the editor',
      'Use the formatting toolbar to style your text',
      'Type or paste your content in the editing area',
      'Click "Save" to preserve your changes',
      'Your content is automatically saved as you type'
    ]
  },
  {
    id: 'search-content',
    title: 'Searching Through Your Content',
    description: 'Find specific content quickly using the search feature.',
    imagePath: '/help-screenshots/search/search-content.png',
    altText: 'Screenshot showing the search interface with search box and results highlighting',
    steps: [
      'Click the search icon in the toolbar',
      'Enter your search terms in the search box',
      'Choose between "Whole Word" or "Contains" search',
      'Toggle case sensitivity if needed',
      'Use "Find Next" and "Find Previous" to navigate results'
    ]
  },
  {
    id: 'semantic-search',
    title: 'Using Semantic Search',
    description: 'Find content using AI-powered semantic search that understands meaning.',
    imagePath: '/help-screenshots/search/semantic-search.png',
    altText: 'Screenshot showing the semantic search window with search results and similarity scores',
    steps: [
      'Open the semantic search panel from the toolbar',
      'Enter your search query in natural language',
      'Toggle semantic search on for AI-powered results',
      'Review results with similarity scores',
      'Click on results to navigate to the content'
    ]
  }
];

export class HelpScreenshotService {
    static getScreenshotsForTopic(topic: string): HelpScreenshot[] {
    const lowerTopic = topic.toLowerCase();
    
    // Map topics to screenshot IDs
    const topicToScreenshots: { [key: string]: string[] } = {
                  // Document Management
                  'load document': ['load-document'],
                  'loading documents': ['load-document'],
                  'load file': ['load-document'],
                  'nbr file': ['load-document'],
                  'import document': ['load-document'],
                  'open file': ['load-document'],
                  'how do i load': ['load-document'],
                  'how to load': ['load-document'],
                  'loading a document': ['load-document'],
                  'load from file': ['load-document'],
                  'load from nbr': ['load-document'],
                  'import from file': ['load-document'],
                  'open nbr file': ['load-document'],
                  
                  'create document': ['create-document'],
                  'new document': ['create-document'],
                  'how to create': ['create-document'],
                  'creating a document': ['create-document'],
                  'how do i create': ['create-document'],
                  'how do i make': ['create-document'],
                  'how to make': ['create-document'],
                  'how to start': ['create-document'],
                  'making a document': ['create-document'],
                  'start new document': ['create-document'],
                  
                  // Category Management
                  'add category': ['add-category'],
                  'create category': ['add-category'],
                  'new category': ['add-category'],
                  'how to add category': ['add-category'],
                  'adding categories': ['add-category'],
                  'organize content': ['add-category'],
                  'how do i add category': ['add-category'],
                  'how to create category': ['add-category'],
                  'how do i create category': ['add-category'],
                  'making categories': ['add-category'],
                  
                  // Content Editing
                  'edit content': ['edit-content'],
                  'content editor': ['edit-content'],
                  'writing content': ['edit-content'],
                  'add content': ['edit-content'],
                  'text editor': ['edit-content'],
                  'rich text': ['edit-content'],
                  'how to edit content': ['edit-content'],
                  'how do i edit content': ['edit-content'],
                  'how to write content': ['edit-content'],
                  'how do i write content': ['edit-content'],
                  'how to add content': ['edit-content'],
                  'how do i add content': ['edit-content'],
                  
                  // Search Features
                  'search content': ['search-content'],
                  'find content': ['search-content'],
                  'search feature': ['search-content'],
                  'how to search': ['search-content'],
                  'finding text': ['search-content'],
                  'how do i search': ['search-content'],
                  'how to find content': ['search-content'],
                  'how do i find content': ['search-content'],
                  'searching for text': ['search-content'],
                  
                  'semantic search': ['semantic-search'],
                  'ai search': ['semantic-search'],
                  'meaning search': ['semantic-search'],
                  'smart search': ['semantic-search'],
                  'how to use semantic search': ['semantic-search'],
                  'how do i use semantic search': ['semantic-search'],
                };

    // Find matching keywords in the topic, prioritizing longer matches
    const matchingScreenshotIds: string[] = [];
    
    // Sort keywords by length (longest first) to prioritize more specific matches
    const sortedKeywords = Object.entries(topicToScreenshots).sort((a, b) => b[0].length - a[0].length);
    
    for (const [keyword, screenshotIds] of sortedKeywords) {
      if (lowerTopic.includes(keyword)) {
        matchingScreenshotIds.push(...screenshotIds);
        // Break after first match to avoid cross-matching
        break;
      }
    }
    
    const screenshots = helpScreenshots.filter(screenshot => 
      matchingScreenshotIds.includes(screenshot.id)
    );
    
    return screenshots;
  }

  static getScreenshotById(id: string): HelpScreenshot | undefined {
    return helpScreenshots.find(screenshot => screenshot.id === id);
  }

  static getAllScreenshots(): HelpScreenshot[] {
    return helpScreenshots;
  }
} 